/* eslint-disable @typescript-eslint/no-explicit-any */
// SHADCN UI
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { v4 as uuidv4 } from "uuid";

import {
	CatalogDiscount,
	CatalogItemVariation,
	CatalogObject,
	CatalogObjectCategory
} from "./square-types";
import { KmcmsRequestError } from "./error-utils";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

// AUTH
import {
	fetchUserAttributes,
	fetchAuthSession,
	decodeJWT,
	signUp,
	confirmSignUp,
	AuthTokens
} from "aws-amplify/auth";
import {
	BarChart2,
	Folders,
	Home,
	LayoutDashboard,
	Package2,
	Settings,
	ShoppingCart,
	Square,
	SquareCheckBig,
	TicketPercent,
	UserCog,
	Users
} from "lucide-react";
import { toast } from "sonner";
import {
	isJwtExpired,
	isSessionAuthFailure,
	notifyAuthExpired
} from "~/lib/auth-utils";

export async function getClientId() {
	const user = await fetchUserAttributes();

	return user["custom:client_id"];
}

export function sortCategories(array: CatalogObjectCategory[]) {
	const sortedData = array.sort((a, b) => {
		const dateA = new Date(a.updated_at);
		const dateB = new Date(b.updated_at);

		return dateB.getTime() - dateA.getTime();
	});

	return sortedData;
}

export function sortProducts(array: CatalogObject[]) {
	const sortedData = array.sort((a, b) => {
		const dateA = new Date(a.updated_at);
		const dateB = new Date(b.updated_at);

		return dateB.getTime() - dateA.getTime();
	});

	return sortedData;
}
export function sortDiscounts(array: CatalogDiscount[]) {
	const sortedData = array.sort((a, b) => {
		const dateA = new Date(a.updated_at);
		const dateB = new Date(b.updated_at);

		return dateB.getTime() - dateA.getTime();
	});

	return sortedData;
}

export function getRFC3339FullDate(date: Date) {
	const year = date.getFullYear();
	const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Month is 0-indexed
	const day = date.getDate().toString().padStart(2, "0");

	return `${year}-${month}-${day}`;
}

export function getDateFromRFC3339FullDate(rfc3339FullDate: string) {
	const [year, month, day] = rfc3339FullDate.split("-").map(Number);
	return new Date(year, month - 1, day); // Month is 0-indexed in JavaScript
}

export function getTimeStringFromDate(date: Date) {
	const hours = date.getHours().toString().padStart(2, "0");
	const minutes = date.getMinutes().toString().padStart(2, "0");
	const seconds = date.getSeconds().toString().padStart(2, "0");

	return `${hours}:${minutes}:${seconds}`;
}

export function getDateFromTimeString(timeString: string) {
	const [hours, minutes, seconds] = timeString.split(":").map(Number);
	const now = new Date(); // Get current date
	return new Date(
		now.getFullYear(),
		now.getMonth(),
		now.getDate(),
		hours,
		minutes,
		seconds
	);
}

export function formatPrice(price: number) {
	const formattedPrice = (price / 100).toFixed(2);
	return formattedPrice;
}

/** Keeps digits and at most one decimal point; optional leading minus for signed amounts (e.g. markups). */
export function sanitizeDecimalInput(
	value: string,
	options?: { allowNegative?: boolean }
): string {
	const allowNegative = options?.allowNegative ?? false;
	let s = value.replace(/[^\d.-]/g, "");
	if (allowNegative) {
		const neg = s.startsWith("-");
		s = s.replace(/-/g, "");
		if (neg) s = "-" + s;
	} else {
		s = s.replace(/-/g, "");
	}
	const firstDot = s.indexOf(".");
	if (firstDot === -1) return s;
	return s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
}

export function sanitizeIntegerInput(value: string): string {
	return value.replace(/\D/g, "");
}

export function findMatchingVariation(
	product: CatalogObject,
	currentOptions: { item_option_id?: string; item_option_value_id?: string }[]
): CatalogItemVariation | undefined {
	if (
		!product ||
		!product?.item_data ||
		product.item_data.variations.length < 1
	) {
		return undefined; // Handle cases where product or variations are missing
	}
	currentOptions.sort((a, b) => {
		if (a.item_option_id && b.item_option_id) {
			return a.item_option_id.localeCompare(b.item_option_id);
		} else if (a.item_option_value_id && b.item_option_value_id) {
			return a.item_option_value_id.localeCompare(b.item_option_value_id);
		} else {
			// Handle the case where both IDs are missing (e.g., return 0)
			return 0;
		}
	});

	for (const variation of product.item_data.variations) {
		if (
			variation.item_variation_data &&
			variation.item_variation_data.item_option_values
		) {
			const variationOptions = variation.item_variation_data.item_option_values;

			// Make a copy of currentOptions for each variation
			const currentOptionsCopy = [...currentOptions];

			if (variationOptions.length !== currentOptionsCopy.length) {
				continue; // Skip to the next variation if lengths are different
			}

			variationOptions.sort((a, b) => {
				if (a.item_option_id && b.item_option_id) {
					return a.item_option_id.localeCompare(b.item_option_id);
				} else if (a.item_option_value_id && b.item_option_value_id) {
					return a.item_option_value_id.localeCompare(b.item_option_value_id);
				} else {
					// Handle the case where both IDs are missing (e.g., return 0)
					return 0;
				}
			});

			let isMatch = true; // Assume a match initially

			for (let i = 0; i < variationOptions.length; i++) {
				if (
					variationOptions[i].item_option_id !==
						currentOptionsCopy[i].item_option_id ||
					variationOptions[i].item_option_value_id !==
						currentOptionsCopy[i].item_option_value_id
				) {
					isMatch = false; // Set to false if a mismatch is found
					break; // No need to continue checking this variation
				}
			}

			// If all elements match, return the variation
			if (isMatch) {
				return variation;
			}
		}
	}
	return undefined;
}

export const authCreateAccount = async (
	email: string,
	password: string,
	first_name: string,
	last_name: string,
	invite: Invite
) => {
	const resp = await signUp({
		username: email,
		password: password,
		options: {
			userAttributes: {
				email: email,
				"custom:client_id": invite.client_id,
				"custom:company_name": invite.company_name,
				given_name: first_name,
				family_name: last_name,
				name: `${first_name} ${last_name}`
			},
			autoSignIn: {
				enabled: true
			}
		}
	}).catch((e: unknown) => {
		const error = handleSignUpError(e as Error);
		return { status: error, nextStep: { signUpStep: "error" } };
	});

	return resp;
};
export const authConfirm = async (code: string, email: string) => {
	const resp = await confirmSignUp({
		username: email,
		confirmationCode: code
	}).catch((e: unknown) => {
		const error = handleConfirmationError(e as Error);
		return { status: error, nextStep: { signUpStep: "error" } };
	});

	return resp;
};

const handleConfirmationError = (err: Error) => {
	let message: string;
	switch (err.name) {
		case "UsernameExistsException":
			message = "An account with the given email already exists.";
			break;
		case "TooManyRequestsException":
			message = "You've made too many attempts. Try again later.";
			break;
		default:
			message = "Error confirming email.";
			console.error(err);
	}
	toast(message);
	throw new Error(message);
};

const handleSignUpError = (err: Error) => {
	let message: string;
	switch (err.name) {
		case "UsernameExistsException":
			message = "An account with the given email already exists.";
			break;
		case "TooManyRequestsException":
			message = "You've made too many attempts. Try again later.";
			break;
		default:
			message = "Error creating account.";
			console.error(err);
	}
	toast(message);
	throw new Error(message);
};

export function toTitleCase(str: string): string {
	return str
		.replaceAll("--", "/")
		.replaceAll("num.", "#")
		.split("_")
		.map((word) => {
			const parts = word.split("/");
			return parts
				.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
				.join("/");
		})
		.join(" ");
}

export const getTokens = async () => {
	try {
		const session = await fetchAuthSession({ forceRefresh: true });
		const idToken = session.tokens?.idToken;
		if (!idToken) {
			notifyAuthExpired();
			return { token: undefined, tokenError: "No current user" };
		}
		return { token: idToken.toString(), tokenError: undefined };
	} catch (e) {
		notifyAuthExpired();
		console.error(e);
		return { token: undefined, tokenError: "Failed to fetch user token" };
	}
};

type RequestParams = {
	endpoint: string;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	payload: any;
	auth?: boolean;
	token?: string;
	request?: Request;
	timeoutMs?: number;
};
export const kmcmsRequest = async ({
	endpoint,
	payload,
	auth = false,
	token = undefined,
	timeoutMs = 30_000
}: RequestParams) => {
	try {
		// boiler plate request options
		let requestOptions = {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization:
					"AWS4-HMAC-SHA256 Credential=AKIAY6DZO7YNE32QHPGU/20231008/us-east-2/execute-api/aws4_request, SignedHeaders=content-length;content-type;host;x-amz-content-sha256;x-amz-date, Signature=c064efd94f21293c8dc31b2a3e6528b0deed9e83a07dd1b254c0679d27fe1e38"
			},
			"Access-Control-Allow-Origin": "*",
			"Access-Control-Allow-Headers": "*",
			redirect: "follow",
			body: ""
		};

		// get tokens for requests
		if (auth && !token && typeof window !== "undefined") {
			const session = await fetchAuthSession();
			token = session.tokens?.idToken?.toString();
			if (!token) {
				notifyAuthExpired();
				throw new KmcmsRequestError(
					"Your session has expired. Please sign in again.",
					{
						statusCode: 401,
						endpoint,
						method: "POST"
					}
				);
			}
		}
		if (auth && !token) {
			const { idToken } = (await fetchAuthSession()).tokens as AuthTokens;
			token = idToken?.toString();
		}
		if (token && typeof window !== "undefined" && isJwtExpired(token)) {
			const session = await fetchAuthSession();
			const refreshed = session.tokens?.idToken?.toString();
			if (refreshed) {
				token = refreshed;
			}
		}
		if (auth && token && !endpoint.startsWith("employees/")) {
			requestOptions["headers"] = {
				...requestOptions["headers"],
				Authorization: token
			};
		}

		if (!auth && !token) {
			try {
				payload["client_id"] = "neebz";
			} catch {
				console.warn("No client id");
			}
		}

		if (!auth && token) {
			const idToken: any = decodeJWT(token);
			payload.client_id = idToken.payload["custom:client_id"];
		}
		if (token && endpoint.startsWith("employees/")) {
			payload.token = token;
		}
		const stringifiedBody = JSON.stringify(payload);

		requestOptions = { ...requestOptions, body: stringifiedBody };

		// make a request to the url provided with the built options
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
		let data: Response;
		try {
			data = await fetch(
				`https://api.cms.amperecreativegroup.com/${endpoint}`,
				{ ...(requestOptions as RequestInit), signal: controller.signal }
			);
		} finally {
			clearTimeout(timeoutId);
		}

		// check for a good response
		if (!data.ok) {
			const errorMessage = await data.text();
			let parsedErrorMessage = errorMessage;
			try {
				const errorData = await JSON.parse(errorMessage);
				parsedErrorMessage =
					errorData?.error ||
					errorData?.errorMessage ||
					errorData?.body ||
					"Something went terribly wrong";
			} catch {
				parsedErrorMessage =
					errorMessage || "An unknown Something went terribly wrong occurred.";
			}
			throw new KmcmsRequestError(parsedErrorMessage, {
				statusCode: data.status,
				statusText: data.statusText,
				endpoint,
				rawBody: errorMessage,
				method: "POST"
			});
		}

		let json;
		json = await data.text();
		try {
			json = JSON.parse(json);
		} catch {
			return json;
		}

		if (typeof json !== "string") {
			// check for an error in lambda
			if (json.errorMessage) {
				throw new KmcmsRequestError(json.errorMessage, {
					statusCode: 500,
					endpoint,
					rawBody:
						typeof json.body === "string" ? json.body : JSON.stringify(json),
					method: "POST"
				});
			}

			if (
				json.statusCode &&
				json.statusCode !== 200 &&
				json.statusCode !== 204
			) {
				const rawBody =
					typeof json.body === "string" ? json.body : JSON.stringify(json.body);
				throw new KmcmsRequestError(
					typeof json.body === "string"
						? json.body
						: "[AWS] External Server Error",
					{
						statusCode: json.statusCode,
						endpoint,
						rawBody,
						method: "POST"
					}
				);
			}
		}
		// return the data from the request
		return json;
	} catch (e: unknown) {
		const normalizedError =
			e instanceof KmcmsRequestError
				? e
				: new KmcmsRequestError(
						(e as Error)?.message || "Unknown request error",
						{
							statusCode: 500,
							endpoint,
							rawBody: JSON.stringify(e),
							method: "POST"
						}
				  );
		if (
			(auth || token) &&
			isSessionAuthFailure(
				`${normalizedError.message} ${normalizedError.rawBody ?? ""}`,
				normalizedError.statusCode
			)
		) {
			notifyAuthExpired();
		} else {
			toast(normalizedError.message);
		}
		console.error(endpoint, normalizedError);
		throw normalizedError;
	}
};

let squareCategoriesListInflight: Promise<CatalogObjectCategory[]> | null =
	null;

/** Concurrent callers share one in-flight request (Strict Mode / duplicate effects). */
export function fetchSquareCategoriesListSorted(): Promise<
	CatalogObjectCategory[]
> {
	if (!squareCategoriesListInflight) {
		squareCategoriesListInflight = (async () => {
			const payload = { show_drafts: true };
			const { token } = await getTokens();
			if (!token) {
				throw new Error("Your session has expired. Please sign in again.");
			}
			const fetchedCategories = await kmcmsRequest({
				endpoint: "square/categories/list",
				payload,
				token: token.toString()
			});
			fetchedCategories.body.categories = sortCategories(
				fetchedCategories.body.categories
			);
			return fetchedCategories.body.categories as CatalogObjectCategory[];
		})().finally(() => {
			squareCategoriesListInflight = null;
		});
	}
	return squareCategoriesListInflight;
}

type RequestMethod = {
	method: "POST" | "GET" | "PUT" | "DELETE" | "PATCH";
	navigate?: boolean;
};

export function toKebabCase(str: string): string {
	return str
		.toLowerCase()
		.replace(/ /g, "_")
		.replaceAll("/", "--")
		.replaceAll("#", "num.");
}

export async function handleActionSubmit(
	data: FormData,
	submit: (data: FormData, method: RequestMethod) => void
) {
	try {
		const { idToken } = (await fetchAuthSession()).tokens ?? {};
		if (!idToken) {
			notifyAuthExpired();
			return;
		}
		data.append("token", idToken.toString());
		submit(data, { method: "POST" });
	} catch {
		notifyAuthExpired();
	}
}

export async function handleActionSubmitWithoutRedirect(
	data: FormData,
	submit: (data: FormData, method: RequestMethod) => void
) {
	try {
		const { idToken } = (await fetchAuthSession()).tokens ?? {};
		if (!idToken) {
			notifyAuthExpired();
			return;
		}
		data.append("token", idToken.toString());
		submit(data, { method: "POST", navigate: false });
	} catch {
		notifyAuthExpired();
	}
}

export const contentTypeIcons = {
	home: Home,
	dashboard: Home,
	team: Users,
	employees: UserCog,
	square: Square
};

export const squareTypeIcons = {
	home: LayoutDashboard,
	orders: ShoppingCart,
	products: Package2,
	discounts: TicketPercent,
	categories: Folders,
	options: SquareCheckBig,
	analytics: BarChart2,
	settings: Settings
};

//IMAGE FILE PROCESSING
import pica from "pica";

export async function processImage(file: File) {
	const MAX_IMAGE_SIZE = 512;
	const bitmap = await createImageBitmap(file);
	const aspect = bitmap.width / Math.max(bitmap.height, 1);
	const canvas = document.createElement("canvas");

	if (bitmap.width >= bitmap.height) {
		if (bitmap.width > MAX_IMAGE_SIZE) {
			canvas.width = MAX_IMAGE_SIZE;
			canvas.height = Math.max(1, Math.ceil(MAX_IMAGE_SIZE / aspect));
		} else {
			canvas.width = Math.max(1, bitmap.width);
			canvas.height = Math.max(1, bitmap.height);
		}
	} else if (bitmap.height > MAX_IMAGE_SIZE) {
		canvas.height = MAX_IMAGE_SIZE;
		canvas.width = Math.max(1, Math.ceil(MAX_IMAGE_SIZE * aspect));
	} else {
		canvas.width = Math.max(1, bitmap.width);
		canvas.height = Math.max(1, bitmap.height);
	}
	const resizeImage = await pica().resize(bitmap, canvas);

	const processedBlob = await pica().toBlob(resizeImage, "image/jpeg");
	const processedFile = new File(
		[processedBlob],
		`processed_image_${uuidv4()}.jpeg`,
		{
			type: "image/jpeg"
		}
	);
	return processedFile;
}

import { fromCognitoIdentityPool } from "@aws-sdk/credential-providers";
import {
	CreateSessionCommand,
	PutObjectCommand,
	S3Client
} from "@aws-sdk/client-s3";
import { Invite } from "~/routes/invite";
import { string } from "zod";

export function generateUUID() {
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

export interface ItemOptionValueData {
	item_option_id: string;
	name: string;
	ordinal?: number;
	km_markup?: number;
	// ... other potential properties of item_option_value_data
}

export interface ItemOptionValue {
	type?: string;
	id?: string;
	version?: number;
	item_option_value_data: ItemOptionValueData;
	image?: File;
	image_url?: string;

	// ... other potential properties of value
}

export interface ItemOptionData {
	name: string;
	display_name?: string;
	show_colors?: boolean;
	values: ItemOptionValue[];
	// ... other potential properties of item_option_data
}

export interface ItemOption {
	type?: string;
	id?: string;
	updated_at?: string;
	created_at?: string;
	version?: number;
	image?: File;
	is_deleted?: boolean;
	present_at_all_locations?: boolean;
	item_option_data: ItemOptionData;
	// ... other potential properties of item_option
}

export function get_product_variations(
	itemOptions: ItemOption[]
): ItemOption[][] {
	/**
	 * Generates all possible combinations of product variations from item options.
	 *
	 * Args:
	 *   itemOptions: An array of ItemOption objects.
	 *
	 * Returns:
	 *   A list of lists, where each inner list represents a unique combination of item options.
	 */
	const allCombinations: ItemOption[][] = [];

	function generateCombinations(
		currentIndex: number,
		currentCombination: ItemOption[]
	): void {
		if (currentIndex === itemOptions.length) {
			allCombinations.push([...currentCombination]);
			return;
		}

		const currentItemOption = itemOptions[currentIndex];

		if (!currentItemOption.item_option_data.values.length) {
			// If no values, return an empty array
			allCombinations.length = 0; // Clear any existing combinations
			return;
		}

		for (let i = 0; i < currentItemOption.item_option_data.values.length; i++) {
			const newItemOption: ItemOption = {
				...currentItemOption, // Copy the existing properties
				item_option_data: {
					...currentItemOption.item_option_data, // Copy the item_option_data
					values: [currentItemOption.item_option_data.values[i]] // Keep only the current value
				}
			};
			currentCombination.push(newItemOption);
			generateCombinations(currentIndex + 1, currentCombination);
			currentCombination.pop();
		}
	}

	generateCombinations(0, []);
	return allCombinations;
}

/** Solve n×n linear system Ax = b (Gaussian elimination with partial pivoting). */
function solveLinearSystemGaussian(
	A: number[][],
	b: number[]
): number[] | null {
	const n = A.length;
	if (n === 0) return [];
	const M = A.map((row, i) => [...row, b[i]]);

	for (let i = 0; i < n; i++) {
		let maxRow = i;
		for (let k = i + 1; k < n; k++) {
			if (Math.abs(M[k][i]) > Math.abs(M[maxRow][i])) {
				maxRow = k;
			}
		}
		[M[i], M[maxRow]] = [M[maxRow], M[i]];
		if (Math.abs(M[i][i]) < 1e-14) {
			return null;
		}
		for (let k = i + 1; k < n; k++) {
			const c = M[k][i] / M[i][i];
			for (let j = i; j <= n; j++) {
				M[k][j] -= c * M[i][j];
			}
		}
	}

	const x = new Array(n).fill(0);
	for (let i = n - 1; i >= 0; i--) {
		x[i] = M[i][n];
		for (let j = i + 1; j < n; j++) {
			x[i] -= M[i][j] * x[j];
		}
		x[i] /= M[i][i];
	}
	return x;
}

/**
 * Least-squares solve for Ax ≈ b where A is m×n (m equations, n unknowns).
 * Uses normal equations (AᵀA + λI)x = Aᵀb.
 */
function leastSquaresMarkups(A: number[][], b: number[]): number[] {
	const m = A.length;
	const n = A[0]?.length ?? 0;
	if (n === 0) return [];
	const AtA: number[][] = Array(n)
		.fill(0)
		.map(() => Array(n).fill(0));
	const Atb: number[] = Array(n).fill(0);

	for (let i = 0; i < n; i++) {
		for (let j = 0; j < n; j++) {
			for (let k = 0; k < m; k++) {
				AtA[i][j] += A[k][i] * A[k][j];
			}
		}
		for (let k = 0; k < m; k++) {
			Atb[i] += A[k][i] * b[k];
		}
	}

	const lambda = 1e-8;
	for (let i = 0; i < n; i++) {
		AtA[i][i] += lambda;
	}

	const sol = solveLinearSystemGaussian(AtA, Atb);
	return sol ?? Array(n).fill(0);
}

/** Lowest variation price in dollars (catalog floor for additive markup math). */
export function getCatalogVariationFloorPriceDollars(
	variations: CatalogItemVariation[] | undefined
): number {
	if (!variations?.length) return 0;
	const amounts = variations
		.map((v) => v.item_variation_data?.price_money?.amount)
		.filter((a): a is number => typeof a === "number");
	return amounts.length > 0 ? Math.min(...amounts) / 100 : 0;
}

/**
 * Infer per-option-value km_markup (dollars, additive to product base) from
 * catalog variation prices. Model: price = basePrice + sum(markups for selected values).
 * Square does not store km_markup; this derives it for the edit UI.
 *
 * Uses the minimum catalog variation price as base (true "floor" when a zero-markup
 * combo exists). Fixes one reference value per option at markup 0 so additive
 * markups are identifiable (avoids splitting phantom +/- offsets across dimensions).
 */
export function inferOptionValueMarkupsFromCatalogVariations(
	itemOptions: ItemOption[],
	catalogVariations: CatalogItemVariation[]
): Map<string, number> | null {
	if (!itemOptions.length || catalogVariations.length === 0) {
		return null;
	}

	const priceAmounts = catalogVariations
		.map((v) => v.item_variation_data?.price_money?.amount)
		.filter((a): a is number => typeof a === "number");
	if (priceAmounts.length === 0) {
		return null;
	}
	const minAmount = Math.min(...priceAmounts);
	const basePriceDollars = minAmount / 100;

	const allValueIds: string[] = [];
	const catalogValueIdSet = new Set<string>();
	for (const option of itemOptions) {
		for (const value of option.item_option_data.values) {
			const id = value.id;
			if (id && !catalogValueIdSet.has(id)) {
				catalogValueIdSet.add(id);
				allValueIds.push(id);
			}
		}
	}
	if (allValueIds.length === 0) {
		return null;
	}

	/** One reference markup = 0 per option; prefer values from a min-price variation. */
	const referenceValueIds = new Set<string>();
	const minRow = catalogVariations.find(
		(cv) => cv.item_variation_data?.price_money?.amount === minAmount
	);
	if (minRow?.item_variation_data?.item_option_values?.length) {
		for (const ov of minRow.item_variation_data.item_option_values) {
			const vid = ov.item_option_value_id;
			if (vid && catalogValueIdSet.has(vid)) {
				referenceValueIds.add(vid);
			}
		}
	}
	for (const option of itemOptions) {
		const refsInOption = option.item_option_data.values.filter(
			(v) => v.id && referenceValueIds.has(v.id)
		);
		if (refsInOption.length > 0) continue;
		const firstWithId = option.item_option_data.values.find((v) => v.id);
		if (firstWithId?.id) {
			referenceValueIds.add(firstWithId.id);
		}
	}

	const freeValueIds: string[] = [];
	const freeIdToIndex = new Map<string, number>();
	for (const option of itemOptions) {
		for (const value of option.item_option_data.values) {
			const id = value.id;
			if (!id || referenceValueIds.has(id)) continue;
			freeIdToIndex.set(id, freeValueIds.length);
			freeValueIds.push(id);
		}
	}
	const nFree = freeValueIds.length;

	const rows: number[][] = [];
	const targets: number[] = [];

	for (const cv of catalogVariations) {
		const ovs = cv.item_variation_data?.item_option_values;
		if (!ovs?.length) continue;

		const row = new Array(nFree).fill(0);
		let allKnown = true;
		for (const ov of ovs) {
			const vid = ov.item_option_value_id;
			if (!vid || !catalogValueIdSet.has(vid)) {
				allKnown = false;
				break;
			}
			const freeIdx = freeIdToIndex.get(vid);
			if (freeIdx !== undefined) {
				row[freeIdx] = 1;
			}
		}
		if (!allKnown) continue;

		const priceDollars =
			(cv.item_variation_data?.price_money?.amount ?? 0) / 100;
		rows.push(row);
		targets.push(priceDollars - basePriceDollars);
	}

	if (rows.length === 0) {
		return null;
	}

	const out = new Map<string, number>();
	for (const id of referenceValueIds) {
		out.set(id, 0);
	}

	if (nFree === 0) {
		for (const id of allValueIds) {
			if (!out.has(id)) out.set(id, 0);
		}
		return out;
	}

	const x = leastSquaresMarkups(rows, targets);
	for (let i = 0; i < nFree; i++) {
		const raw = x[i] ?? 0;
		const m = Math.round(raw * 100) / 100;
		out.set(freeValueIds[i], m);
	}

	for (const id of allValueIds) {
		if (!out.has(id)) out.set(id, 0);
	}
	return out;
}

/**
 * Copy of item options with km_markup filled from catalog variation prices (edit flow).
 */
export function mergeItemOptionsWithInferredMarkups(
	itemOptions: ItemOption[],
	product: CatalogObject
): ItemOption[] {
	const catalogVariations = product.item_data?.variations;
	if (!catalogVariations?.length || !itemOptions.length) {
		return itemOptions;
	}

	const map = inferOptionValueMarkupsFromCatalogVariations(
		itemOptions,
		catalogVariations
	);
	if (!map || map.size === 0) {
		return itemOptions;
	}

	return itemOptions.map((opt) => ({
		...opt,
		item_option_data: {
			...opt.item_option_data,
			values: opt.item_option_data.values.map((v) => {
				if (!v.id) return v;
				const m = map.get(v.id);
				if (m === undefined) return v;
				return {
					...v,
					item_option_value_data: {
						...v.item_option_value_data,
						km_markup: m
					}
				};
			})
		}
	}));
}

export function getImgSource(files: (File | undefined | string)[]) {
	/**
	 * Sets the source of an img element to the first File object in an array.
	 *
	 * Args:
	 *   img: The img element to set the source of.
	 *   files: An array of possibly undefined and File objects.
	 */
	for (const file of files) {
		if (file instanceof File) {
			return file;
		} else if (typeof file === "string") {
			return file;
		}
	}

	return undefined;
}

export function isHttpUrl(value: unknown): value is string {
	return typeof value === "string" && /^https?:\/\//i.test(value.trim());
}

export async function uploadSEOImage(
	file: File | Blob,
	name: string,
	index: number = 0,
	bucket = "kmcms-public"
) {
	const S3_ENDPOINT = "https://kmcms-public.s3.us-east-2.amazonaws.com";
	const IDENTITY_POOL_ID = "us-east-2:2ad66d04-f53d-4702-91ff-bf6cf4da258a";
	const BUCKET_REGION = "us-east-2";

	if (IDENTITY_POOL_ID) {
		const credentials = fromCognitoIdentityPool({
			identityPoolId: IDENTITY_POOL_ID,
			clientConfig: { region: BUCKET_REGION }
		});

		// Update the AWS credentials

		const client = new S3Client({ region: BUCKET_REGION, credentials });

		const key = `permanent/${await getClientId()}/${toKebabCase(name)}.jpeg`;
		const location = `${S3_ENDPOINT}/${key}`;

		try {
			const sessionCommand = new CreateSessionCommand({
				SessionMode: "ReadWrite",
				Bucket: bucket
			});

			const putCommand = new PutObjectCommand({
				ContentType: file.type,
				Bucket: bucket,
				Body: file,
				Key: key
			});

			await client.send(sessionCommand);
			await client.send(putCommand);

			return location as string;
		} catch (err) {
			console.error("[uploadSEOImage]", err);
			throw err instanceof Error ? err : new Error("Failed to upload SEO image");
		}
	}
	throw new Error("Image upload is not configured");
}

export async function uploadImage(
	file: File | Blob,
	index: number = 0,
	bucket = "kmcms-public"
) {
	const S3_ENDPOINT = "https://kmcms-public.s3.us-east-2.amazonaws.com";
	const IDENTITY_POOL_ID = "us-east-2:2ad66d04-f53d-4702-91ff-bf6cf4da258a";
	const BUCKET_REGION = "us-east-2";

	if (IDENTITY_POOL_ID) {
		const credentials = fromCognitoIdentityPool({
			identityPoolId: IDENTITY_POOL_ID,
			clientConfig: { region: BUCKET_REGION }
		});

		// Update the AWS credentials

		const client = new S3Client({ region: BUCKET_REGION, credentials });

		const IMAGE_TYPE = file.type.replace("image/", "") || "jpeg";
		const key = `${await getClientId()}/${new Date().toISOString()}-${index}.${IMAGE_TYPE}`;
		const location = `${S3_ENDPOINT}/${key}`;

		try {
			const sessionCommand = new CreateSessionCommand({
				SessionMode: "ReadWrite",
				Bucket: bucket
			});

			const putCommand = new PutObjectCommand({
				ContentType: file.type || "image/jpeg",
				Bucket: bucket,
				Body: file,
				Key: key
			});

			await client.send(sessionCommand);
			await client.send(putCommand);

			return location as string;
		} catch (err) {
			console.error("[uploadImage]", err);
			throw err instanceof Error ? err : new Error("Failed to upload image");
		}
	}
	throw new Error("Image upload is not configured");
}
